/**
 * API client for the Image Index — INVI's cross-dealer photo coverage view
 * and per-vehicle drill-in. Wraps /image-index/* endpoints plus the
 * operator-side repush at /photo-capture/vehicles/{vin}/repush.
 *
 * Response shapes mirror backend/schemas.py exactly. If the backend
 * changes a field, change this file in the same commit.
 */
import { API } from "../config/api"

const TOKEN_KEY = "invi_token"

function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ""
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    // Prefer the JSON `detail` field — FastAPI's standard error shape — so
    // operators see "Image-processing worker is unreachable" rather than the
    // raw `{"detail":"..."}` envelope. Falls back to plain text body, then
    // to the HTTP status when neither is available.
    let message = `HTTP ${res.status}`
    try {
      const body = await res.clone().json()
      if (typeof body?.detail === "string") {
        message = body.detail
      } else {
        const text = await res.text()
        if (text) message = text
      }
    } catch {
      try {
        const text = await res.text()
        if (text) message = text
      } catch {
        // Keep the HTTP status fallback.
      }
    }
    throw new Error(message)
  }
  return res
}

// --- Response shapes (mirror backend/schemas.py) ---

export interface RooftopOption {
  slug: string
  name: string
}

// Vendor-feed sync status. Drives the UI chip on Image Index rows and the
// Vehicle Detail header. Mirrors `IN_FEED_STATUS_*` constants in
// invi/schemas/core.py.
export type InFeedStatus = "in_feed" | "not_in_feed" | "no_feed_configured"

export interface ImageIndexVehicle {
  session_id: number
  // VIN is nullable: stock-only items (CTA campers etc.) have no VIN.
  // When null, use `stock` as the row's primary identifier in the UI.
  vin: string | null
  stock: string | null
  // Decoded from VIN at session-create time via NHTSA's vPIC API.
  // Null for VINs that don't decode (off-road builds, custom vehicles).
  year: number | null
  make: string | null
  model: string | null
  rooftop_slug: string
  rooftop_name: string
  photo_count: number
  captured_at: string | null
  pushed_at: string | null
  // Generic export status: capturing | uploading | ready | pushed | failed.
  // Destination-neutral so the same field works for any rooftop's exporter
  // (CTA's WordPress today, Homenet for the rest of the dealer group).
  status: string
  in_feed_status: InFeedStatus
}

// Per-photo AI processing lifecycle. Mirrors `PHOTO_PROCESSING_STATUS_*` in
// invi/schemas/core.py. `null` means no job has ever touched this photo
// (legacy uploads from before the pipeline shipped, sessions that haven't
// been processed yet).
export type PhotoProcessingStatus =
  | "pending"
  | "processing"
  | "processed"
  | "failed"
  | "unmatched"

export interface IndexPhoto {
  id: number
  display_order: number
  original_url: string
  // AI-processed draft of `original_url` — the worker's most recent run,
  // awaiting operator approval. When present, the gallery shows it first.
  // Cleared by approval (promoted into `processed_url`) or by a rerun.
  pending_processed_url: string | null
  // The committed processed image — what approval promoted and what the
  // export path ships. Mirrors `IndexPhoto.processed_url` in backend
  // schemas.py.
  processed_url: string | null
  processing_status: PhotoProcessingStatus | null
  captured_at: string | null
  exported_at: string | null
}

export interface VehicleDetail {
  vin: string | null
  stock: string | null
  year: number | null
  make: string | null
  model: string | null
  rooftop_slug: string
  rooftop_name: string
  session_id: number | null
  photographer_username: string | null
  photo_count: number
  captured_at: string | null
  pushed_at: string | null
  status: string | null
  last_export_error: string | null
  in_feed_status: InFeedStatus
}

export interface DealerCaptureCount {
  rooftop_slug: string
  rooftop_name: string
  session_count: number
}

export interface PhotosOverview {
  range_from: string
  range_to: string
  total_captured_sessions: number
  sessions_completed: number
  units_captured: number
  shoots_complete: number
  shoots_partial: number
  sessions_pushed: number
  sessions_ready_for_push: number
  sessions_failed: number
  captures_per_dealer: DealerCaptureCount[]
}

export interface OverviewQuery {
  from?: string    // YYYY-MM-DD
  to?: string      // YYYY-MM-DD
  rooftop?: string  // rooftop slug; omit for all rooftops
  dealerGroupId?: number | null
}

// SessionResponse from /photo-capture/vehicles/{vin}/repush.
export interface RepushResult {
  id: number
  rooftop_id: number
  vin: string
  stock: string | null
  photographer_user_id: number
  status: string
  client_session_uuid: string
  started_at: string
  completed_at: string | null
  pushed_at: string | null
  export_attempts: number
  export_last_error: string | null
}

// --- Fetchers ---

export async function listRooftops(dealerGroupId?: number | null): Promise<RooftopOption[]> {
  const qs = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await authFetch(`${API.baseUrl}${API.endpoints.imageIndex.rooftops}${qs}`)
  return res.json()
}

export interface ListVehiclesParams {
  rooftop?: string
  hasPhotos?: boolean
  limit?: number
  dealerGroupId?: number | null
}

export async function listVehicles(params: ListVehiclesParams = {}): Promise<ImageIndexVehicle[]> {
  const search = new URLSearchParams()
  if (params.rooftop !== undefined) search.set("rooftop", params.rooftop)
  if (params.hasPhotos !== undefined) search.set("has_photos", String(params.hasPhotos))
  if (params.limit !== undefined) search.set("limit", String(params.limit))
  if (params.dealerGroupId != null) search.set("dealer_group_id", String(params.dealerGroupId))
  const qs = search.toString()
  const url = `${API.baseUrl}${API.endpoints.imageIndex.vehicles}${qs ? `?${qs}` : ""}`
  const res = await authFetch(url)
  return res.json()
}

export async function getVehicleDetail(vin: string, rooftop: string): Promise<VehicleDetail> {
  const url = `${API.baseUrl}${API.endpoints.imageIndex.vehicleDetail(vin)}?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url)
  return res.json()
}

export async function getVehiclePhotos(vin: string, rooftop: string): Promise<IndexPhoto[]> {
  const url = `${API.baseUrl}${API.endpoints.imageIndex.vehiclePhotos(vin)}?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url)
  return res.json()
}

/**
 * By-stock variants for inventory items that have no VIN (CTA campers,
 * trailers, accessory bundles). Same response shapes as the by-VIN
 * endpoints — the by-stock route is reached when the user clicks into
 * a row whose `vin` is null on the Image Index.
 */
export async function getItemDetail(stock: string, rooftop: string): Promise<VehicleDetail> {
  const url = `${API.baseUrl}${API.endpoints.imageIndex.itemDetail(stock)}?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url)
  return res.json()
}

export async function getItemPhotos(stock: string, rooftop: string): Promise<IndexPhoto[]> {
  const url = `${API.baseUrl}${API.endpoints.imageIndex.itemPhotos(stock)}?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url)
  return res.json()
}

export async function deleteAllItemPhotos(
  stock: string,
  rooftop: string,
): Promise<DeleteAllPhotosResult> {
  const url = `${API.baseUrl}/image-index/items/${encodeURIComponent(stock)}/photos?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url, { method: "DELETE" })
  return res.json()
}

export async function downloadItemPhotosZip(stock: string, rooftop: string): Promise<Blob> {
  const url = `${API.baseUrl}/image-index/items/${encodeURIComponent(stock)}/photos.zip?rooftop=${encodeURIComponent(rooftop)}`
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()}` },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `HTTP ${res.status}`)
  }
  return res.blob()
}

export async function getOverview(query: OverviewQuery = {}): Promise<PhotosOverview> {
  const search = new URLSearchParams()
  if (query.from) search.set("from", query.from)
  if (query.to) search.set("to", query.to)
  if (query.rooftop) search.set("rooftop", query.rooftop)
  if (query.dealerGroupId != null) search.set("dealer_group_id", String(query.dealerGroupId))
  const qs = search.toString()
  const url = `${API.baseUrl}${API.endpoints.imageIndex.overview}${qs ? `?${qs}` : ""}`
  const res = await authFetch(url)
  return res.json()
}

export interface DeleteAllPhotosResult {
  deleted: number
  failed: number
  session_id: number
}

export async function deleteAllVehiclePhotos(
  vin: string,
  rooftop: string,
): Promise<DeleteAllPhotosResult> {
  const url = `${API.baseUrl}/image-index/vehicles/${encodeURIComponent(vin)}/photos?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url, { method: "DELETE" })
  return res.json()
}

export interface ReorderPhotosResult {
  photos: IndexPhoto[]
}

export async function reorderVehiclePhotos(
  vin: string,
  rooftop: string,
  photoIds: number[],
): Promise<ReorderPhotosResult> {
  const url = `${API.baseUrl}/image-index/vehicles/${encodeURIComponent(vin)}/photos/order?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url, {
    method: "PATCH",
    body: JSON.stringify({ photo_ids: photoIds }),
  })
  return res.json()
}

export async function reorderItemPhotos(
  stock: string,
  rooftop: string,
  photoIds: number[],
): Promise<ReorderPhotosResult> {
  const url = `${API.baseUrl}/image-index/items/${encodeURIComponent(stock)}/photos/order?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url, {
    method: "PATCH",
    body: JSON.stringify({ photo_ids: photoIds }),
  })
  return res.json()
}

// Which stages of the worker pipeline a job should run. `all` is the full
// pipeline (background → enhancement → plate → shot type → reorder); the
// other values let the QC page rerun a single stage from a draft. Mirrors
// `IMAGE_PROCESSING_FILTER_*` in invi/schemas/core.py.
export type ProcessFilter = "all" | "background" | "enhancement" | "plate" | "reorder"

// Result of POST /image-index/.../process. The job is already in `pending`
// state when this returns; the worker transitions it through
// running → done asynchronously. Poll `getProcessingJob` until status is
// `done` or `failed` for a durable completion signal — relying on photo
// states alone gives a false "done" while the worker is cold-starting.
export interface ProcessPhotosResult {
  job_id: number
  session_id: number
  status: string
  filter: ProcessFilter
  photos_total: number
}

// Top-level state for an image-processing job. Mirrors
// `IMAGE_PROCESSING_JOB_STATUS_*` in invi/schemas/core.py.
export type ProcessingJobStatusValue = "pending" | "running" | "done" | "failed"

// Polling snapshot for an in-flight or finished job. `error_text` carries the
// top-level failure reason when status is `failed` — per-photo failures live
// in IndexPhoto.processing_error instead.
export interface ProcessingJobSnapshot {
  job_id: number
  session_id: number
  status: ProcessingJobStatusValue
  filter: ProcessFilter
  photos_total: number
  photos_done: number
  photos_failed: number
  error_text: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
}

// `force` resets already-processed photos so the worker re-runs them with
// the current pipeline instead of skipping them (its skip guard exists for
// crash-retry safety). The caller confirms with the operator first — see
// VehicleDetailPage's handleProcessClick.
export async function processVehiclePhotos(
  vin: string,
  rooftop: string,
  filter: ProcessFilter = "all",
  force: boolean = false,
): Promise<ProcessPhotosResult> {
  const url = `${API.baseUrl}${API.endpoints.imageIndex.processVehicle(vin)}?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url, {
    method: "POST",
    body: JSON.stringify({ filter, force }),
  })
  return res.json()
}

export async function processItemPhotos(
  stock: string,
  rooftop: string,
  filter: ProcessFilter = "all",
  force: boolean = false,
): Promise<ProcessPhotosResult> {
  const url = `${API.baseUrl}${API.endpoints.imageIndex.processItem(stock)}?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url, {
    method: "POST",
    body: JSON.stringify({ filter, force }),
  })
  return res.json()
}

// Result of POST /image-index/.../photos/approve — mirrors
// ApprovePhotosResponse in backend schemas.py. `approved` counts drafts
// promoted to committed this call; zero is a valid no-op.
export interface ApprovePhotosResult {
  session_id: number
  approved: number
}

export async function approveVehiclePhotos(
  vin: string,
  rooftop: string,
): Promise<ApprovePhotosResult> {
  const url = `${API.baseUrl}${API.endpoints.imageIndex.approveVehiclePhotos(vin)}?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url, { method: "POST" })
  return res.json()
}

export async function approveItemPhotos(
  stock: string,
  rooftop: string,
): Promise<ApprovePhotosResult> {
  const url = `${API.baseUrl}${API.endpoints.imageIndex.approveItemPhotos(stock)}?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url, { method: "POST" })
  return res.json()
}

export async function getVehicleProcessingJob(
  vin: string,
  jobId: number,
  rooftop: string,
): Promise<ProcessingJobSnapshot> {
  const url = `${API.baseUrl}${API.endpoints.imageIndex.vehicleJob(vin, jobId)}?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url)
  return res.json()
}

export async function getItemProcessingJob(
  stock: string,
  jobId: number,
  rooftop: string,
): Promise<ProcessingJobSnapshot> {
  const url = `${API.baseUrl}${API.endpoints.imageIndex.itemJob(stock, jobId)}?rooftop=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url)
  return res.json()
}

export async function downloadVehiclePhotosZip(vin: string, rooftop: string): Promise<Blob> {
  const url = `${API.baseUrl}/image-index/vehicles/${encodeURIComponent(vin)}/photos.zip?rooftop=${encodeURIComponent(rooftop)}`
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()}` },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `HTTP ${res.status}`)
  }
  return res.blob()
}

export async function repushVehicle(vin: string, rooftop: string): Promise<RepushResult> {
  const url = `${API.baseUrl}${API.endpoints.photoCapture.repush(vin)}?rooftop_slug=${encodeURIComponent(rooftop)}`
  const res = await authFetch(url, { method: "POST" })
  return res.json()
}

export interface UpdateSessionFields {
  rooftop_slug?: string
  // VIN can be cleared (set to null) when reclassifying a previously
  // VIN'd session as a stock-only item — e.g. an operator realizes a
  // vehicle was misclassified.
  vin?: string | null
  stock?: string | null
  year?: number | null
  make?: string | null
  model?: string | null
}

export async function updateSession(
  sessionId: number,
  fields: UpdateSessionFields,
): Promise<RepushResult> {
  const url = `${API.baseUrl}/photo-capture/sessions/${sessionId}`
  const res = await authFetch(url, {
    method: "PATCH",
    body: JSON.stringify(fields),
  })
  return res.json()
}
